import { useState, useEffect } from 'react';
import { ToggleSwitch } from '@/components/ui/toggle-switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useUpdateFailover } from '../../hooks';
import { ShieldAlert, TriangleAlert } from 'lucide-react';
import type { DID } from '../../types';

interface FailoverTabProps {
  did?: DID | null;
}

export function FailoverTab({ did }: FailoverTabProps) {
  const [enabled, setEnabled] = useState(false);
  const [failoverNumber, setFailoverNumber] = useState('');
  const updateFailover = useUpdateFailover();

  useEffect(() => {
    if (did) {
      setEnabled(!!did.has_failover);
      setFailoverNumber(did.failover || '');
    }
  }, [did]);

  const handleSubmit = () => {
    if (!did) return;
    updateFailover.mutate({
      tn: did.tn,
      enabled,
      failoverNumber: enabled ? failoverNumber : undefined,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="p-3 bg-red-50 rounded-xl">
          <ShieldAlert className="h-6 w-6 text-red-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Failover Routing</h3>
          <p className="text-sm text-slate-500 mt-1">
            Configure a backup number for calls when the primary destination is unreachable.
          </p>
        </div>
      </div>

      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
        <TriangleAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-800">Mutual Exclusivity</p>
          <p className="text-sm text-amber-700">
            Only one of the following services may be enabled at a time:{' '}
            <strong>Forward, Failover, eFax, and Conference</strong>
          </p>
        </div>
      </div>

      {/* Toggle Card */}
      <div className="bg-slate-50 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-slate-900">Failover Routing</p>
            <p className="text-sm text-slate-500">
              {enabled ? 'Backup routing is active' : 'No backup routing configured'}
            </p>
          </div>
          <ToggleSwitch
            enabled={enabled}
            onChange={setEnabled}
          />
        </div>

        {enabled && (
          <div className="pt-4 border-t border-slate-200">
            <Input
              label="Failover Number"
              placeholder="Enter 10-digit phone number"
              value={failoverNumber}
              onChange={(e) => setFailoverNumber(e.target.value.replace(/\D/g, ''))}
              maxLength={10}
              hint="Calls will route here if the primary destination fails"
            />
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end pt-2">
        <Button
          onClick={handleSubmit}
          isLoading={updateFailover.isPending}
          disabled={!did || (enabled && failoverNumber.length !== 10)}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
}
